// frontend/src/pages/P2PHub.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

import P2PMarket from "./P2PMarket";
import MyP2PTrades from "./MyP2PTrades";
import PostAd from "./PostAd";
import MyAds from "./MyAds";
import P2PProfile from "./P2PProfile";

const TABS = [
  { id: "market", label: "Browse Market", emoji: "🛒" },
  { id: "myTrades", label: "My Trades", emoji: "🤝" },
  { id: "postAd", label: "Post an Ad", emoji: "➕" },
  { id: "myAds", label: "My Ads", emoji: "📋" },
  { id: "profile", label: "My Profile", emoji: "👤" },
];

export default function P2PHub() {
  const location = useLocation();
  const navigate = useNavigate();

  // ---------- Tab state with URL + localStorage persistence ----------
  const urlTab = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const t = q.get("tab");
    return TABS.some((x) => x.id === t) ? t : null;
  }, [location.search]);

  const [activeTab, setActiveTab] = useState(
    urlTab || localStorage.getItem("p2pActiveTab") || "market"
  );

  useEffect(() => {
    if (urlTab && urlTab !== activeTab) setActiveTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  useEffect(() => {
    localStorage.setItem("p2pActiveTab", activeTab);
  }, [activeTab]);

  const setAndSyncTab = (id) => {
    setActiveTab(id);
    const q = new URLSearchParams(location.search);
    q.set("tab", id);
    navigate({ search: q.toString() }, { replace: true });
  };

  // Keep tab chip scrolled into view (desktop top tabs)
  const navRef = useRef(null);
  useEffect(() => {
    const el = navRef.current?.querySelector(`button[data-tab="${activeTab}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeTab]);

  // Keyboard navigation for tabs (Left/Right/Home/End) - for desktop top tabs
  const onTabsKeyDown = (e) => {
    const idx = TABS.findIndex((t) => t.id === activeTab);
    if (idx < 0) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setAndSyncTab(TABS[(idx + 1) % TABS.length].id);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setAndSyncTab(TABS[(idx - 1 + TABS.length) % TABS.length].id);
    } else if (e.key === "Home") {
      e.preventDefault();
      setAndSyncTab(TABS[0].id);
    } else if (e.key === "End") {
      e.preventDefault();
      setAndSyncTab(TABS[TABS.length - 1].id);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "market":
        return <P2PMarket />;
      case "myTrades":
        return <MyP2PTrades />;
      case "postAd":
        return <PostAd />;
      case "myAds":
        return <MyAds />;
      case "profile":
        return <P2PProfile />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-950"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 shadow-sm border-b border-blue-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          {/* Top row */}
          <div className="flex items-start md:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-blue-900 dark:text-blue-200 leading-tight">
                P2P Hub
              </h1>
              <p className="text-blue-600 dark:text-blue-400 text-xs md:text-base mt-0.5">
                Buy & sell crypto directly with peers
              </p>
            </div>

            {/* Desktop Quick Actions (right side) */}
            <div className="hidden md:flex items-center gap-2">
              <QAButton label="Deposit" onClick={() => navigate("/deposit")} />
              <QAButton label="Withdraw" onClick={() => navigate("/withdraw")} />
              <QAButton label="Quick Trade" onClick={() => navigate("/quick-trade")} />
              <QAButton label="Exchange" onClick={() => navigate("/exchange")} />
            </div>
          </div>

          {/* Mobile Quick Actions (below title) */}
          <div className="mt-3 md:hidden">
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              <QAButton label="Deposit" onClick={() => navigate("/deposit")} />
              <QAButton label="Withdraw" onClick={() => navigate("/withdraw")} />
              <QAButton label="Quick Trade" onClick={() => navigate("/quick-trade")} />
              <QAButton label="Exchange" onClick={() => navigate("/exchange")} />
            </div>
          </div>

          {/* Desktop Tabs (compact, professional) */}
          <motion.nav
            ref={navRef}
            role="tablist"
            aria-label="P2P sections"
            onKeyDown={onTabsKeyDown}
            className="hidden md:block mt-5"
            initial={{ y: 6, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
<div className="w-full rounded-xl bg-blue-50 dark:bg-slate-800 p-1 border border-blue-100 dark:border-slate-700">
  <div className="grid grid-cols-5 w-full gap-1">
    {TABS.map((t) => (
      <TopTabButton
        key={t.id}
        id={`tab-${t.id}`}
        dataTab={t.id}
        active={activeTab === t.id}
        onClick={() => setAndSyncTab(t.id)}
        full
      >
        <span className="mr-2">{t.emoji}</span>
        <span className="whitespace-nowrap">{t.label}</span>
      </TopTabButton>
    ))}
  </div>
</div>
          </motion.nav>
        </div>
      </header>

      {/* Main */}
      <main
        className={
          // extra bottom padding on mobile because bottom nav is fixed
          "max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8 " +
          "pb-24 md:pb-10"
        }
      >
        <motion.section
          id={`panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
          className="relative overflow-hidden bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-blue-100 dark:border-slate-800 p-4 md:p-6"
          key={activeTab}
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.18 }}
        >
          {renderContent()}
        </motion.section>
      </main>

      {/* Mobile Bottom Tabs (permanent icons like Binance) */}
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-40"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
      >
        <div className="bg-white/95 dark:bg-slate-950/95 backdrop-blur border-t border-blue-100 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-2">
            <nav role="tablist" aria-label="P2P sections bottom tabs" className="grid grid-cols-5">
              {TABS.map((t) => (
                <BottomTabButton
                  key={t.id}
                  id={`bt-${t.id}`}
                  active={activeTab === t.id}
                  onClick={() => setAndSyncTab(t.id)}
                  label={t.label}
                  emoji={t.emoji}
                />
              ))}
            </nav>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ----------------- Small components ----------------- */

function QAButton({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-blue-600 text-white px-3 py-2 text-xs rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap active:scale-[0.99]"
      type="button"
    >
      {label}
    </button>
  );
}

function TopTabButton({ id, dataTab, active, onClick, children }) {
  return (
    <button
      id={id}
      data-tab={dataTab}
      role="tab"
      aria-selected={active}
      aria-controls={`panel-${dataTab}`}
      onClick={onClick}
      type="button"
      className={
        "px-3 py-2 rounded-lg text-sm font-medium transition flex items-center " +
        (active
          ? "bg-white dark:bg-slate-900 text-blue-900 dark:text-blue-200 shadow-sm border border-blue-100 dark:border-slate-700"
          : "text-blue-700 dark:text-blue-300 hover:bg-white/70 dark:hover:bg-slate-900/60")
      }
    >
      {children}
    </button>
  );
}

function BottomTabButton({ active, onClick, label, emoji, id }) {
  return (
    <button
      id={id}
      role="tab"
      aria-selected={active}
      onClick={onClick}
      type="button"
      className={
        "py-3 flex flex-col items-center justify-center gap-1 transition " +
        (active ? "text-blue-700 dark:text-blue-300" : "text-slate-600 dark:text-slate-400")
      }
    >
      <div
        className={
          "w-10 h-8 rounded-xl flex items-center justify-center text-base " +
          (active ? "bg-blue-50 dark:bg-slate-800" : "bg-transparent")
        }
      >
        <span aria-hidden="true">{emoji}</span>
      </div>
      <div className={"text-[11px] font-medium leading-none " + (active ? "" : "opacity-90")}>
        {label}
      </div>
    </button>
  );
}

/* Small utility (Tailwind plugin not required; class is safe even if unused elsewhere) */
